<template>
  <b-row class="recurrence-info-section">
    <b-col cols="12" md="8" class="d-flex flex-column gap-3">
      <!-- Start Date -->
      <b-row>
        <b-col cols="12" md="4">
          <p class="recurrence-info-section__info-label">Tanggal Mulai</p>
        </b-col>
        <b-col cols="12" md="8">
          <FormulateInput
            class="p-0"
            name="startDate"
            type="date-picker"
            validation="bail|required|dateLteField:endDate"
            validation-behavior="live"
            :validation-messages="{
              dateLteField: 'Tanggal Mulai tidak boleh lebih dari Tanggal Berakhir'
            }"
            value-type="YYYY-MM-DDTHH:mm:ss.SSSZ"
            placeholder="Pilih tanggal"
            format="DD MMM YYYY"
            :disabled="isLoading"
            :help="true"
            :disabled-date="(date) => date < new Date(new Date().setHours(0, 0, 0, 0))"
          />
        </b-col>
      </b-row>

      <!-- recurrence Type -->
      <b-row>
        <b-col cols="12" md="4">
          <p class="recurrence-info-section__info-label">Ulangi Setiap</p>
        </b-col>
        <b-col cols="12" md="3">
          <FormulateInput
            class="p-0"
            name="recurrenceValue"
            type="text"
            _type="number"
            placeholder="Ulangi Setiap"
            validation="bail|number|min:1,value|max:99,value"
            :validation-messages="{
              number: 'Interval harus berupa angka',
              min: 'Interval tidak kurang dari 1',
              max: 'Interval tidak lebih dari 99',
            }"
            :disabled="isLoading"
          />
        </b-col>
        <b-col cols="12" md="5">
          <FormulateInput
            class="p-0"
            name="recurrenceType"
            type="select"
            :options="recurrenceTypes"
            :disabled="isLoading"
          />
        </b-col>

        <!-- WEEKLY Options -->
        <b-col
          v-if="formValues?.recurrenceType === 'WEEKLY'"
          cols="12"
          offset-md="4"
          class="d-flex mt-2 gap-2"
        >
          <slot name="weekly-checkbox" />
        </b-col>

        <!-- MONTHLY Options -->
        <template v-if="formValues?.recurrenceType === 'MONTHLY'">
          <b-col
            cols="12"
            md="3"
            offset-md="4"
            class="mt-2 d-flex flex-column justify-content-center"
          >
            <p class="recurrence-info-section__info-label">Tanggal</p>
          </b-col>
          <b-col cols="12" md="2" class="mt-2">
            <FormulateInput
              class="p-0"
              name="selectedMonthlyDate"
              type="text"
              _type="number"
              placeholder="Pilih tanggal"
              validation="bail|number|min:1,value|max:31,value"
              :validation-messages="{
                number: 'Tanggal harus berupa angka',
                min: 'Tanggal tidak kurang dari 1',
                max: 'Tanggal tidak lebih dari 31',
              }"
              :disabled="isLoading"
            />
          </b-col>
        </template>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <p class="recurrence-info-section__info-label">Jam</p>
        </b-col>
        <b-col cols="12" md="3">
          <FormulateInput
            class="p-0"
            name="recurrenceTime"
            type="date-picker"
            datepickerType="time"
            format="HH:mm"
            icon="icon-outline_clock"
            placeholder="Pilih jam"
            :disabled-time="disableTime"
            :disabled="isLoading"
          />
        </b-col>
      </b-row>

      <!-- End Date -->
      <b-row>
        <b-col cols="12" md="4">
          <p class="recurrence-info-section__info-label">Tanggal Berakhir</p>
        </b-col>
        <b-col cols="12" md="8">
          <FormulateInput
            class="p-0"
            name="endDate"
            type="date-picker"
            placeholder="Pilih tanggal berakhir"
            validation="bail|required|dateGteField:startDate"
            validation-behavior="live"
            :validation-messages="{
              dateGteField: 'Tanggal Berakhir tidak boleh kurang dari Tanggal Mulai'
            }"
            value-type="YYYY-MM-DDTHH:mm:ss.SSSZ"
            format="DD MMM YYYY"
            :help="true"
            :disabled-date="(date) => date < new Date(new Date().setHours(0, 0, 0, 0))"
            :disabled="isLoading"
          />
        </b-col>
      </b-row>

      <!-- End Date -->
      <b-row>
        <b-col cols="12" md="4">
          <p class="recurrence-info-section__info-label">Jadwal Selanjutnya</p>
        </b-col>
        <b-col cols="12" md="3" class="d-flex gap-2 align-items-center">
          <div>{{ computedNextRun }}</div>
          <i
            v-if="['Tidak Aktif', 'Gagal Dijalankan'].includes(status)"
            v-b-tooltip.hover
            :title="tooltipText"
            class="recurrence-info-section__warning-icon icon-fill_alert-triangle"
          />
        </b-col>
      </b-row>
    </b-col>

    <b-col cols="12">
      <b-skeleton v-if="isLoading"></b-skeleton>
      <McAlert
        v-else-if="formValues.endDate"
        withIcon
        class="mt-3"
        variant="info"
        iconVariant="fill"
        :text="recurrenceData?.recurrence_translate"
      />
    </b-col>
  </b-row>
</template>

<script>
import moment from 'moment'
import McAlert from '@/components/McAlert'

import { recurrenceTypes } from '../lib/constants/mappings.js'

export default {
  components: {
    McAlert,
  },
  inject: ['isLoading'],
  props: {
    formValues: {},
    recurrenceData: {
      type: Object,
      default: () => {},
    },
    status: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      recurrenceTypes,
    }
  },
  computed: {
    computedNextRun() {
      if (!this.recurrenceData?.next_run) return '-'

      return moment(this.recurrenceData?.next_run).format('DD MMMM YYYY')
    },
    tooltipText() {
      if (this.status === 'Gagal Dijalankan') {
        return 'Penjadwalan gagal dijalankan'
      }

      return 'Penjadwalan sedang tidak aktif'
    },
  },
  methods: {
    disableTime(date) {
      const startDate = moment(this.formValues.startDate)

      if (startDate.isAfter(moment(), 'day')) {
        return false
      }

      const optionDate = moment(date)
      if (optionDate.isSameOrAfter(moment())) {
        return false
      }

      return true
    },
  },
}
</script>

<style lang="scss">
.recurrence-info-section {
  &__info-label {
    margin: 0;
    color: $mep-color-text-300;
  }

  &__warning-icon {
    font-size: 16px;

    &:before {
      color: $yellow;
    }
  }
}
</style>
